/**
 * Action Button API
 *
 * Fetches template data when user clicks an action-button.
 * The endpoint and method come from the button's action contract.
 * Response structure matches chat stream FINAL_RESPONSE (AssistantMessage inside `response`).
 */
import type { AssistantMessage } from '@contracts';
import { buildHeaders, buildQueryParams, buildUrl } from '@/api/utils';
import { CHAT_ENDPOINTS } from '@/api/endpoints';

export interface ActionButtonApiResponse {
  response: AssistantMessage;
}

interface CbErrorDetails {
  errorMessage?: string;
  errorDescription?: string;
}

/**
 * Thrown when the action button API returns a non-2xx response with CB error body.
 */
export class ActionButtonApiError extends Error {
  readonly errorMessage: string;
  readonly errorDescription: string;

  constructor(details: CbErrorDetails) {
    super(details.errorDescription ?? details.errorMessage ?? 'API error');
    this.name = 'ActionButtonApiError';
    this.errorMessage = details.errorMessage ?? '';
    this.errorDescription = details.errorDescription ?? '';
  }
}

function parseCbErrorDetails(body: unknown): CbErrorDetails | null {
  if (typeof body === 'object' && body !== null && 'errorDetails' in body) {
    const { errorDetails } = body as { errorDetails?: CbErrorDetails };
    if (errorDetails?.errorMessage || errorDetails?.errorDescription) {
      return errorDetails;
    }
  }
  return null;
}

export async function fetchActionButtonTemplate(
  method: 'GET' | 'POST',
  params?: Record<string, string>,
): Promise<ActionButtonApiResponse> {
  const endpoint = CHAT_ENDPOINTS.POLL_RESULT;
  const isGet = method === 'GET';
  const url = isGet && params ? buildUrl(endpoint, buildQueryParams(params)) : buildUrl(endpoint);

  const response = await fetch(url, {
    method,
    headers: buildHeaders(),
    ...(!isGet && params && { body: JSON.stringify(params) }),
  });

  if (!response.ok) {
    try {
      const errorBody: unknown = await response.json();
      const details = parseCbErrorDetails(errorBody);
      if (details) {
        throw new ActionButtonApiError(details);
      }
    } catch (error: unknown) {
      if (error instanceof ActionButtonApiError) throw error;
    }
    throw new Error(`Action button API failed: ${response.status}`);
  }

  return response.json() as Promise<ActionButtonApiResponse>;
}
